import React, { useState, useEffect } from 'react'
import { BrowserRouter, useSearchParams } from 'react-router'
import { TranslationProvider } from '../../shared/TranslationProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { Calendar } from '../OutplacementBenefits/Calendar'
import { getApi } from '~/util/api'
import { Typography, Tabs } from '~/components/_atoms'
import { Stack, Page } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import translations from '../locales.json'
import WebinarListItem from './WebinarListItem'
import { useMediaQueryScreen } from '~/hooks/useMediaQuery'
import EmailSignUpModal from '~/components/home/cards/EmailSignUpModal'
import { Toaster } from '~/lib/ui/toast'
import PostWebinarResourceListItem from './PostWebinarResourceListItem'

const EMPTY_EVENTS = {
  events: [],
  attended_webinars: [],
  currentUser: null,
}

const UpcomingEvents = ({
  live_events_notifications_on,
  is_partner_candidate,
  is_premium_or_select,
  show_disclaimer,
  is_us_select,
  locale,
}) => {
  const { t } = useTranslation()
  const isPartnerCandidate = is_partner_candidate
  const isPremiumOrSelect = is_premium_or_select
  const isUsSelect = is_us_select
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [calendarEvents, setCalendarEvents] = useState(EMPTY_EVENTS)
  const isLargeScreen = useMediaQueryScreen('lg')
  const listRef = React.useRef(null)
  const showDisclaimer = show_disclaimer

  const [currentMode, setCurrentMode] = useState(() => {
    const modeFromParams = searchParams.get('mode')
    if (window.innerWidth >= 1024) {
      return modeFromParams || 'calendar'
    }
    return modeFromParams || 'list'
  })

  const [currentType, setCurrentType] = useState(() => {
    return searchParams.get('type') || 'all'
  })

  const selectedEventId = searchParams.get('event_id')

  useEffect(() => {
    const modeFromParams = searchParams.get('mode')
    if (isLargeScreen) {
      setCurrentMode(modeFromParams || 'calendar')
    } else {
      setCurrentMode(modeFromParams || 'list')
    }
  }, [isLargeScreen, searchParams])

  const fetchUpcomingWebinars = async () => {
    setIsLoading(true)

    try {
      const response = await getApi().get('/coach_services/upcoming_webinars')

      if (response.status !== 200) {
        throw new Error('json request failed')
      }

      setCalendarEvents(response.data)
    } catch (error) {
      console.log('json request failed', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUpcomingWebinars()
  }, [])

  const filteredEvents = React.useMemo(() => {
    const allEvents = calendarEvents.events || []
    const attendedWebinars = calendarEvents.attended_webinars || []

    const today = new Date()
    today.setUTCHours(0, 0, 0, 0)
    const todayTime = today.getTime()

    const events =
      currentMode === 'post-webinar'
        ? attendedWebinars
        : currentMode === 'list'
          ? allEvents.filter((event) => {
              const eventDate = new Date(event.start_at)
              eventDate.setUTCHours(0, 0, 0, 0)
              const eventTime = eventDate.getTime()

              return !event.is_past || eventTime === todayTime
            })
          : allEvents

    switch (currentType) {
      case 'ace-the-interview':
        return events.filter((event) => event.is_ace_the_interview_webinar)
      case 'avoiding-ageism':
        return events.filter((event) => event.is_ageism_webinar)
      case 'qa-ai-job-search':
        return events.filter((event) => event.is_coach_qa_ai_job_search_webinar)
      case 'qa-job-search-strategy':
        return events.filter(
          (event) => event.is_coach_qa_job_search_strategy_webinar,
        )
      case 'jumpstart':
        return events.filter((event) => event.is_jumpstart_webinar)
      case 'linkedin':
        return events.filter((event) => event.is_linkedin_webinar)
      case 'linkedin-visibility':
        return events.filter((event) => event.is_linkedin_visibility_webinar)
      case 'negotiating-success':
        return events.filter((event) => event.is_negotiating_success_webinar)
      case 'networking':
        return events.filter((event) => event.is_networking_webinar)
      case 'resume':
        return events.filter((event) => event.is_resume_webinar)
      case 'wellness':
        return events.filter((event) => event.is_wellness_webinar)
      case 'career-reset':
        return events.filter((event) => event.is_your_career_reset_webinar)
      case 'gec-events':
        return events.filter((event) => event.is_gec_event)
      case 'linkedin-201':
        return events.filter(
          (event) => event.is_linkedin_features_visibility_webinar,
        )
      case 'all':
      default:
        return events
    }
  }, [calendarEvents, currentMode, currentType])

  const handleTypeChange = (event) => {
    const newType = event.target.value
    setCurrentType(newType)

    const newSearchParams = new URLSearchParams(searchParams)
    if (newType === 'all') {
      newSearchParams.delete('type')
    } else {
      newSearchParams.set('type', newType)
    }
    setSearchParams(newSearchParams)
  }

  const handleModeChange = (newMode) => {
    setCurrentMode(newMode)

    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('mode', newMode)
    setSearchParams(newSearchParams)
  }

  useEffect(() => {
    if (
      !isLoading &&
      currentMode === 'list' &&
      selectedEventId &&
      listRef.current
    ) {
      const selectedElement = listRef.current.querySelector(
        `[data-event-id="${selectedEventId}"]`,
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [isLoading, currentMode, selectedEventId, filteredEvents])

  const tabs = [
    { key: 'calendar', label: t('tabs.calendar'), icon: 'calendar' },
    { key: 'list', label: t('tabs.list'), icon: 'list' },
  ]

  if (!isPartnerCandidate) {
    tabs.push({
      key: 'post-webinar',
      label: t('tabs.post_webinar_resources'),
      icon: 'sample-doc',
    })
  }

  const description = isPremiumOrSelect
    ? t('premium_or_select_description')
    : t('description')

  return (
    <>
      <Toaster />
      <Page
        title={t('title')}
        header={
          <Typography>
            {description}{' '}
            {!isPartnerCandidate && (
              <>
                <EmailSignUpModal
                  linkText={t('sign_up')}
                  hasLiveEventsNotifications={live_events_notifications_on}
                />
                {'.'}
              </>
            )}
          </Typography>
        }
        variant="coach-services"
      >
        <Stack spacing={3}>
          {isLoading ? (
            <div className="flex items-center justify-center">
              <Spinner show className="h-9 w-9" />
            </div>
          ) : (
            <>
              <div
                className={`flex items-center ${isLargeScreen ? 'justify-between' : 'justify-end'}`}
              >
                {isLargeScreen && (
                  <Tabs
                    items={tabs}
                    selectedItem={currentMode}
                    onSelect={handleModeChange}
                    variant="secondary"
                  />
                )}
                {!isPartnerCandidate && (
                  <select
                    value={currentType}
                    onChange={handleTypeChange}
                    aria-label={t('filters.label')}
                    className={`inline-block min-w-[260px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${isLargeScreen ? 'ml-4' : ''}`}
                  >
                    <option value="all">{t('filters.all')}</option>
                    <option value="ace-the-interview">
                      {t('filters.ace-the-interview')}
                    </option>
                    <option value="avoiding-ageism">
                      {t('filters.avoiding-ageism')}
                    </option>
                    <option value="qa-ai-job-search">
                      {t('filters.qa-ai-job-search')}
                    </option>
                    <option value="linkedin-visibility">
                      {t('filters.linkedin-visibility')}
                    </option>
                    {isUsSelect && (
                      <option value="gec-events">
                        {t('filters.gec-events')}
                      </option>
                    )}
                    <option value="qa-job-search-strategy">
                      {t('filters.qa-job-search-strategy')}
                    </option>
                    <option value="jumpstart">{t('filters.jumpstart')}</option>
                    <option value="linkedin">{t('filters.linkedin')}</option>
                    <option value="linkedin-201">
                      {t('filters.linkedin-201')}
                    </option>
                    <option value="negotiating-success">
                      {t('filters.negotiating-success')}
                    </option>
                    <option value="networking">
                      {t('filters.networking')}
                    </option>
                    <option value="resume">{t('filters.resume')}</option>
                    <option value="wellness">{t('filters.wellness')}</option>
                    <option value="career-reset">
                      {t('filters.career-reset')}
                    </option>
                  </select>
                )}
              </div>

              <div className="mt-4">
                {currentMode === 'calendar' && isLargeScreen && (
                  <div className="relative">
                    <Calendar
                      calendar_events={filteredEvents}
                      homePageDisplay={false}
                      opRedesign={true}
                      coachDashboardDisplay={false}
                      locale={locale}
                    />
                  </div>
                )}

                {currentMode === 'list' &&
                  (filteredEvents.length === 0 ? (
                    <Typography className="mt-8 text-center text-gray-500">
                      {t('no_events_message')}
                    </Typography>
                  ) : (
                    <div ref={listRef}>
                      <Stack spacing={6}>
                        {filteredEvents.map((event) => (
                          <WebinarListItem
                            key={`event_${event.id}`}
                            webinar={event}
                            currentUser={calendarEvents.currentUser}
                            selected={selectedEventId === event.id.toString()}
                            data-event-id={event.id}
                            isPartnerCandidate={isPartnerCandidate}
                            showDisclaimer={showDisclaimer}
                          />
                        ))}
                      </Stack>
                    </div>
                  ))}

                {currentMode === 'post-webinar' &&
                  (filteredEvents.length > 0 ? (
                    <div>
                      {filteredEvents.map((resource) => (
                        <PostWebinarResourceListItem
                          key={resource.id}
                          resource={resource}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
                      <div className="col-span-1 mt-5 text-center md:col-span-3">
                        <img
                          src="https://www-yns-com.s3.us-east-2.amazonaws.com/images/no_favorites.png"
                          alt="No Resources"
                          className="mx-auto mb-6"
                        />
                        <h2 className="mb-4">{t('post_webinar_resources')}</h2>
                        <p>{t('no_resources_available_yet')}</p>
                      </div>
                      <div className="min-w-[472px] overflow-hidden"></div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </Stack>
      </Page>
    </>
  )
}

export default function UpcomingEventsWithProviders({ locale, ...props }) {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.coach_services.UpcomingEvents"
    >
      <BrowserRouter>
        <UpcomingEvents locale={locale} {...props} />
      </BrowserRouter>
    </TranslationProvider>
  )
}
